import { useQuery } from '@tanstack/react-query';
import { reportsService } from '../services/reportsService';
import type {
  LocationSummaryFilters,
  ManufacturerPerformanceFilters,
  AgingExceptionsFilters,
  UsageComplianceFilters,
  DailyReceivingFilters,
  PatientOrderTrackingFilters,
  FrameTurnaroundFilters,
  OrderEfficiencyFilters,
} from '../services/reportsService';

/**
 * Query key factory for reports
 */
export const reportsKeys = {
  all: ['reports'] as const,
  locationSummary: (filters: LocationSummaryFilters) =>
    [...reportsKeys.all, 'location-summary', filters] as const,
  manufacturerPerformance: (filters: ManufacturerPerformanceFilters) =>
    [...reportsKeys.all, 'manufacturer-performance', filters] as const,
  agingExceptions: (filters: AgingExceptionsFilters) =>
    [...reportsKeys.all, 'aging-exceptions', filters] as const,
  usageCompliance: (filters: UsageComplianceFilters) =>
    [...reportsKeys.all, 'usage-compliance', filters] as const,
  dailyReceiving: (filters: DailyReceivingFilters) =>
    [...reportsKeys.all, 'daily-receiving', filters] as const,
  patientOrderTracking: (filters: PatientOrderTrackingFilters) =>
    [...reportsKeys.all, 'patient-order-tracking', filters] as const,
  frameTurnaround: (filters: FrameTurnaroundFilters) =>
    [...reportsKeys.all, 'frame-turnaround', filters] as const,
  orderEfficiency: (filters: OrderEfficiencyFilters) =>
    [...reportsKeys.all, 'order-efficiency', filters] as const,
};

/**
 * Hook to fetch location summary report
 */
export function useLocationSummary(filters: LocationSummaryFilters) {
  return useQuery({
    queryKey: reportsKeys.locationSummary(filters),
    queryFn: () => reportsService.getLocationSummary(filters),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to fetch manufacturer performance report
 */
export function useManufacturerPerformance(filters: ManufacturerPerformanceFilters) {
  return useQuery({
    queryKey: reportsKeys.manufacturerPerformance(filters),
    queryFn: () => reportsService.getManufacturerPerformance(filters),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to fetch aging exceptions report
 */
export function useAgingExceptions(filters: AgingExceptionsFilters) {
  return useQuery({
    queryKey: reportsKeys.agingExceptions(filters),
    queryFn: () => reportsService.getAgingExceptions(filters),
    staleTime: 1000 * 60 * 2, // 2 minutes (more dynamic data)
  });
}

/**
 * Hook to fetch usage compliance report
 */
export function useUsageCompliance(filters: UsageComplianceFilters) {
  return useQuery({
    queryKey: reportsKeys.usageCompliance(filters),
    queryFn: () => reportsService.getUsageCompliance(filters),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to fetch daily receiving report
 */
export function useDailyReceiving(filters: DailyReceivingFilters) {
  return useQuery({
    queryKey: reportsKeys.dailyReceiving(filters),
    queryFn: () => reportsService.getDailyReceiving(filters),
    staleTime: 1000 * 60 * 2, // 2 minutes (more dynamic data)
  });
}

/**
 * Hook to fetch patient order tracking report
 */
export function usePatientOrderTracking(filters: PatientOrderTrackingFilters) {
  return useQuery({
    queryKey: reportsKeys.patientOrderTracking(filters),
    queryFn: () => reportsService.getPatientOrderTracking(filters),
    staleTime: 1000 * 60 * 2, // 2 minutes (more dynamic data)
  });
}

/**
 * Hook to fetch frame turnaround report
 */
export function useFrameTurnaround(filters: FrameTurnaroundFilters) {
  return useQuery({
    queryKey: reportsKeys.frameTurnaround(filters),
    queryFn: () => reportsService.getFrameTurnaround(filters),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to fetch order efficiency report
 */
export function useOrderEfficiency(filters: OrderEfficiencyFilters) {
  return useQuery({
    queryKey: reportsKeys.orderEfficiency(filters),
    queryFn: () => reportsService.getOrderEfficiency(filters),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}
